import { hoursToSeconds } from 'date-fns';

import {
    RECORDING_LOCALIZED_FIELDS,
    RECORDING_MAIN_METADATA_LANGUAGE,
} from 'config/api.ts';
import {
    DETAIL_DELETE_RECORDING,
    DETAIL_DELETE_SERIES,
    DETAIL_PROTECT_RECORDING,
    DETAIL_RECORD_BUTTON,
    DETAIL_RECORD_SERIES,
    DETAIL_UNPROTECT_RECORDING,
} from 'config/testID';

import {
    CachedRecordingItem,
    QuotaUsage,
    RecordingItem,
    RecordingMetadata,
    RecordingStatus,
} from 'store/recordings/reducer.types';

import { CdvrRecording } from 'services/data-manager/types';
import { components } from 'services/data-manager/types.railsbuilder';

import {
    SCREEN_DETAIL_CANCEL_BUTTON,
    SCREEN_DETAIL_RECORDING_DELETE_BUTTON,
    SCREEN_DETAIL_RECORDING_DELETE_SERIES_BUTTON,
    SCREEN_DETAIL_RECORDING_PROTECT_BUTTON,
    SCREEN_DETAIL_RECORDING_UNPROTECT_BUTTON,
    SCREEN_DETAIL_RECORD_BUTTON,
    SCREEN_DETAIL_SERIES_CANCEL_EPISODE_BUTTON,
    SCREEN_DETAIL_SERIES_CANCEL_SERIES_BUTTON,
    SCREEN_DETAIL_SERIES_RECORD_EPISODE_BUTTON,
    SCREEN_DETAIL_SERIES_RECORD_SERIES_BUTTON,
} from 'screens/Detail/languageConst';
import {
    ManageRecordingItem,
    ManageRecordingItemKeys,
} from 'screens/Detail/types';

import LANGUAGES_NAMES from './Language/languageNames';

// TODOO where to put constants like this?
export const RECORDING_BUTTONS: {
    [key in keyof typeof ManageRecordingItemKeys]: ManageRecordingItem;
} = {
    ScheduleRecording: {
        key: ManageRecordingItemKeys.ScheduleRecording,
        label: SCREEN_DETAIL_RECORD_BUTTON,
        testID: DETAIL_RECORD_BUTTON,
    },
    CancelRecording: {
        key: ManageRecordingItemKeys.CancelRecording,
        label: SCREEN_DETAIL_CANCEL_BUTTON,
        testID: DETAIL_RECORD_BUTTON,
    },
    ProtectRecording: {
        key: ManageRecordingItemKeys.ProtectRecording,
        label: SCREEN_DETAIL_RECORDING_PROTECT_BUTTON,
        testID: DETAIL_PROTECT_RECORDING,
    },
    UnprotectRecording: {
        key: ManageRecordingItemKeys.UnprotectRecording,
        label: SCREEN_DETAIL_RECORDING_UNPROTECT_BUTTON,
        testID: DETAIL_UNPROTECT_RECORDING,
    },
    DeleteRecording: {
        key: ManageRecordingItemKeys.DeleteRecording,
        label: SCREEN_DETAIL_RECORDING_DELETE_BUTTON,
        testID: DETAIL_DELETE_RECORDING,
    },
    ScheduleEpisode: {
        key: ManageRecordingItemKeys.ScheduleEpisode,
        label: SCREEN_DETAIL_SERIES_RECORD_EPISODE_BUTTON,
        testID: DETAIL_RECORD_BUTTON,
    },
    CancelEpisode: {
        key: ManageRecordingItemKeys.CancelEpisode,
        label: SCREEN_DETAIL_SERIES_CANCEL_EPISODE_BUTTON,
        testID: DETAIL_RECORD_BUTTON,
    },

    ScheduleSeries: {
        key: ManageRecordingItemKeys.ScheduleSeries,
        label: SCREEN_DETAIL_SERIES_RECORD_SERIES_BUTTON,
        testID: DETAIL_RECORD_SERIES,
    },
    CancelSeries: {
        key: ManageRecordingItemKeys.CancelSeries,
        label: SCREEN_DETAIL_SERIES_CANCEL_SERIES_BUTTON,
        testID: DETAIL_RECORD_SERIES,
    },
    DeleteSeries: {
        key: ManageRecordingItemKeys.DeleteSeries,
        label: SCREEN_DETAIL_RECORDING_DELETE_SERIES_BUTTON,
        testID: DETAIL_DELETE_SERIES,
    },
};

export const hasRemainingProtectedSpace = (
    eventDuration: number,
    quota: QuotaUsage | null,
): boolean => {
    // Maximum protected time allowed is 72 hours
    // TODO: change if BE change object structure

    const allowedProtectedQuota = hoursToSeconds(72);
    const usedProtectedQuota = parseInt(
        quota?.usageSummary.protectedRecordings ?? '',
        10,
    );

    return eventDuration <= allowedProtectedQuota - usedProtectedQuota;
};

export const isScheduled = (
    recordingItem?: RecordingItem | CachedRecordingItem,
) => !!recordingItem && recordingItem.status === RecordingStatus.SCHEDULED;

export const isRecorded = (
    recordingItem?: RecordingItem | CachedRecordingItem,
) =>
    !!recordingItem &&
    (recordingItem?.status === RecordingStatus.RECORDED ||
        (recordingItem?.status === RecordingStatus.RECORDING &&
            new Date(recordingItem?.captureEnd) < new Date()));

export const isRecording = (
    recordingItem?: RecordingItem | CachedRecordingItem,
) => !!recordingItem && recordingItem.status === RecordingStatus.RECORDING;

export const isFailed = (recordingItem?: RecordingItem | CachedRecordingItem) =>
    recordingItem?.status === RecordingStatus.FAILED;

export const isValidRecording = (
    recordingItem?: RecordingItem | CachedRecordingItem,
) => !!recordingItem && !isFailed(recordingItem);

export const isProtectedRecording = (
    recordingItem?: RecordingItem | CachedRecordingItem,
) => !!recordingItem && recordingItem.isProtected;

export const hasSeriesRecording = (
    recordingItem?: RecordingItem | CachedRecordingItem,
) => !!recordingItem?.seriesRecordingId;

export const selectRecordingMetadataBaseOnLanguage = (
    item: RecordingItem['metadata'],
    currentLanguage: keyof typeof LANGUAGES_NAMES,
): RecordingMetadata => {
    const languageItems = Object.entries(item || {}).map(([key, value]) => ({
        languageKey: key.split('_')[0],
        value,
    }));

    const metadataValueBasedOnLanguage = languageItems.find(
        ({ languageKey }) => languageKey === currentLanguage,
    )?.value;

    if (metadataValueBasedOnLanguage) {
        return RECORDING_LOCALIZED_FIELDS.reduce(
            (acc, field) => {
                if (metadataValueBasedOnLanguage[field] != null) {
                    return {
                        ...acc,
                        [field]: metadataValueBasedOnLanguage[field],
                    };
                }

                return acc;
            },
            { ...item?.[RECORDING_MAIN_METADATA_LANGUAGE] },
        );
    }

    if (item?.[RECORDING_MAIN_METADATA_LANGUAGE]) {
        return item[RECORDING_MAIN_METADATA_LANGUAGE];
    }

    return { id: '' };
};

// Used in rails response only.
// In rails request there is no way of setting `fields` query param, so we will
// merge hu_HU metadata with the currently selected language metadata.
export const selectCdvrRecordingMetadataBaseOnLanguage = (
    item: CdvrRecording['metadata'],
    currentLanguage: keyof typeof LANGUAGES_NAMES,
): components['schemas']['Metadata'] => {
    const languageItems = Object.entries(item || {}).map(([key, value]) => ({
        languageKey: key.split('_')[0],
        value,
    }));

    const metadataValueBasedOnLanguage = languageItems.find(
        ({ languageKey }) => languageKey === currentLanguage,
    )?.value;

    if (metadataValueBasedOnLanguage) {
        return Object.keys(metadataValueBasedOnLanguage).reduce(
            (acc, field) => {
                if (metadataValueBasedOnLanguage[field] != null) {
                    return {
                        ...acc,
                        [field]: metadataValueBasedOnLanguage[field],
                    };
                }

                return acc;
            },
            { ...item?.[RECORDING_MAIN_METADATA_LANGUAGE] },
        );
    }

    if (item?.[RECORDING_MAIN_METADATA_LANGUAGE]) {
        return item[RECORDING_MAIN_METADATA_LANGUAGE];
    }

    return {};
};

export function isRecordingItem(
    maybeRecording: any,
): maybeRecording is RecordingItem {
    return maybeRecording?.metadata != null;
}

export function getDefaultMetadata(
    recording: RecordingItem,
): RecordingMetadata | undefined {
    return recording.metadata?.[RECORDING_MAIN_METADATA_LANGUAGE];
}
